import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useCalculatorStore } from '@/store/calculator';
import { addMonths, addYears, format } from 'date-fns';
import ReactDOM from 'react-dom';
import { cn } from '@/lib/utils';

interface FutureDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultDate: number;
}

export function FutureDateModal({ isOpen, onClose, defaultDate }: FutureDateModalProps) {
  const { updateValue } = useCalculatorStore();
  const [selectedDate, setSelectedDate] = useState<number>(defaultDate);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Lock scroll when modal is open
  useEffect(() => {
    if (!isOpen) return;
    
    // Store original body styles
    const originalStyle = window.getComputedStyle(document.body);
    const originalOverflow = originalStyle.overflow;
    
    // Save current scroll position
    const scrollY = window.scrollY;
    
    // Apply scroll lock
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Restore original styles and scroll position
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.overflow = originalOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Calculate preset dates
  const sixMonthsOut = addMonths(new Date(), 6).getTime();
  const oneYearOut = addYears(new Date(), 1).getTime();
  const twoYearsOut = addYears(new Date(), 2).getTime();
  const fiveYearsOut = addYears(new Date(), 5).getTime();

  // Format date for display
  const getFormattedDate = (timestamp: number) => {
    return format(new Date(timestamp), 'MMMM d, yyyy');
  };

  // Format date for input value
  const getFormattedDateValue = (timestamp: number) => {
    return format(new Date(timestamp), 'yyyy-MM-dd');
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateStr = e.target.value;
    const dateObj = new Date(dateStr);
    if (!isNaN(dateObj.getTime())) {
      setSelectedDate(dateObj.getTime());
    }
  };

  const applyDate = () => {
    updateValue('date', selectedDate);
    onClose();
  };

  // Calculate min and max dates
  const todayStr = format(new Date(), 'yyyy-MM-dd'); // Format as YYYY-MM-DD
  
  // Calculate the maximum allowed date (30 years in the future)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 30);
  const maxDateStr = format(maxDate, 'yyyy-MM-dd'); // Format as YYYY-MM-DD

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[100]"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div 
        ref={modalRef}
        className="fixed z-[100] bg-white rounded-lg shadow-xl w-full max-w-md"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Set Your Target Purchase Date
            </h2>
            <button
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200"
              onClick={onClose}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Information */}
          <div className="mb-4 p-3 bg-[#1A5173]/10 rounded-lg border border-[#1A5173]/20">
            <p className="text-sm text-gray-600">
              The "Required Savings" calculator needs a future purchase date to determine how much you need to save each month. 
              Please select a date at least a few months in the future.
            </p>
          </div>

          {/* Date Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Purchase Date:
            </label>
            <input
              type="date"
              value={getFormattedDateValue(selectedDate)}
              onChange={handleDateChange}
              min={todayStr}
              max={maxDateStr}
              className={cn(
                "w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
              )}
            />
          </div>

          {/* Quick selection presets */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Select:
            </label>
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => setSelectedDate(sixMonthsOut)}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700">
                6 months
              </button>
              <button 
                onClick={() => setSelectedDate(oneYearOut)}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700">
                1 year
              </button>
              <button 
                onClick={() => setSelectedDate(twoYearsOut)}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700">
                2 years
              </button>
              <button 
                onClick={() => setSelectedDate(fiveYearsOut)}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700">
                5 years
              </button>
            </div>
          </div>

          {/* Selected Date Display */}
          <div className="mb-6 p-3 bg-gray-50 rounded-lg text-center">
            <div className="text-sm text-gray-500">Selected Date:</div>
            <div className="text-lg font-medium text-gray-900">{getFormattedDate(selectedDate)}</div>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={applyDate}
              className="px-4 py-2 text-white bg-primary rounded hover:bg-primary-dark"
            >
              Set Date
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}